// Purpose: /s/:token — recipient progressive access flow.
// Commit 8b.14.1 reordered the steps so the recipient stays in their email
// app for the link + access-code reads, then switches once for the TOTP:
//   step 1: email      — verify they have access to the inbox the share was sent to
//   step 2: access code — enter the 8-digit code from the second pre-share email
//                         (server emails the TOTP at this point)
//   step 3: TOTP        — enter the 6-digit code from the verification email
//                         (server mints the download grant cookie)

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { MonitoredNotice } from '../components/legal/MonitoredNotice.tsx';
import { Button, Card, Input } from '../components/ui/index.ts';
import { PadlockIcon, ArrowRightIcon } from '../components/icons/index.tsx';

interface Meta {
  name: string | null;
  expiresAt: string | null;
  step: 'email' | 'access_code' | 'totp';
}

type Stage = 'email' | 'access_code' | 'totp';

export function RecipientLanding() {
  const { token } = useParams<{ token: string }>();
  const [meta, setMeta] = useState<Meta | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fatalReason, setFatalReason] = useState<'gone' | 'not_found' | null>(null);
  const [stage, setStage] = useState<Stage>('email');
  const [submitting, setSubmitting] = useState(false);

  // Per-stage inputs
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [totp, setTotp] = useState('');

  // Step 2 metadata
  const [totpExpiresInSeconds, setTotpExpiresInSeconds] = useState<number | null>(null);

  useEffect(() => {
    if (!token) return;
    axios
      .get<Meta>(`/api/public/share/${token}/meta`, { withCredentials: true })
      .then((res) => {
        setMeta(res.data);
        setStage(res.data.step);
      })
      .catch((err) => {
        const status = (err as { response?: { status?: number } }).response?.status;
        if (status === 410) setFatalReason('gone');
        else if (status === 404) setFatalReason('not_found');
        else setError('Unable to load share.');
      });
  }, [token]);

  async function submitEmail(e?: React.FormEvent) {
    e?.preventDefault();
    if (!token) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError('Enter the email address this share was sent to.');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await axios.post(
        `/api/public/share/${token}/verify-email`,
        { email: email.trim() },
        { withCredentials: true }
      );
      setStage('access_code');
    } catch (err) {
      const ax = err as { response?: { status?: number } };
      if (ax.response?.status === 401) {
        setError('Verification failed. Please check the email address and try again.');
      } else {
        setError('Verification failed.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function submitAccessCode(e?: React.FormEvent) {
    e?.preventDefault();
    if (!token) return;
    if (!/^\d{8}$/.test(code)) {
      setError('Enter the 8-digit access code.');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const res = await axios.post<{ ok: true; totpSent: true; expiresInSeconds: number }>(
        `/api/public/share/${token}/verify-access-code`,
        { accessCode: code },
        { withCredentials: true }
      );
      setTotpExpiresInSeconds(res.data.expiresInSeconds);
      setStage('totp');
    } catch (err) {
      const ax = err as {
        response?: { status?: number; data?: { error?: string; attemptsRemaining?: number } };
      };
      const reason = ax.response?.data?.error;
      if (reason === 'email_validation_required') {
        setError('Your email verification expired. Start over from step 1.');
        setStage('email');
      } else if (reason === 'locked') {
        setError('Too many incorrect attempts. Try again in an hour.');
      } else if (reason === 'totp_rate_limited') {
        setError('Too many verification codes requested recently. Wait an hour.');
      } else if (reason === 'totp_send_failed') {
        setError('Code accepted but we could not email the verification code. Try again.');
      } else if (ax.response?.status === 401) {
        const remaining = ax.response.data?.attemptsRemaining ?? 0;
        setError(`Incorrect code. ${remaining} attempt${remaining === 1 ? '' : 's'} remaining.`);
      } else {
        setError('Verification failed.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function submitTotp(e?: React.FormEvent) {
    e?.preventDefault();
    if (!token) return;
    if (!/^\d{6}$/.test(totp)) {
      setError('Enter the 6-digit code.');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await axios.post(
        `/api/public/share/${token}/verify-totp`,
        { totpCode: totp },
        { withCredentials: true }
      );
      window.location.href = `/s/${token}/files`;
    } catch (err) {
      const ax = err as { response?: { status?: number; data?: { error?: string } } };
      const reason = ax.response?.data?.error;
      if (reason === 'access_code_required') {
        setError('Your access-code step expired. Re-enter your access code.');
        setStage('access_code');
      } else if (reason === 'expired') {
        setError('That code expired. Click "Resend code" to request a new one.');
      } else if (reason === 'exhausted') {
        setError('Too many wrong attempts. Click "Resend code" to request a new one.');
      } else {
        setError('Incorrect code.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function resendTotp() {
    if (!token) return;
    setError(null);
    setSubmitting(true);
    try {
      const res = await axios.post<{ ok: true; expiresInSeconds: number }>(
        `/api/public/share/${token}/resend-totp`,
        null,
        { withCredentials: true }
      );
      setTotpExpiresInSeconds(res.data.expiresInSeconds);
    } catch (err) {
      const ax = err as { response?: { status?: number; data?: { error?: string } } };
      const reason = ax.response?.data?.error;
      if (reason === 'access_code_required') {
        setError('Your access-code step expired. Re-enter your access code.');
        setStage('access_code');
      } else if (reason === 'totp_rate_limited') {
        setError('Too many code requests. Wait up to an hour, then try again.');
      } else {
        setError('Could not resend the code. Try again in a moment.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (fatalReason === 'gone') {
    return (
      <FatalScreen
        title="Share unavailable"
        message="This share has expired or been revoked. Contact the sender for a new link."
      />
    );
  }
  if (fatalReason === 'not_found') {
    return (
      <FatalScreen
        title="Link not valid"
        message="This share link doesn't match anything on file. Check the URL or ask the sender to resend."
      />
    );
  }

  if (!meta) {
    return <p className="text-body-m text-fg-secondary">Loading…</p>;
  }

  const stepNum = stage === 'email' ? 1 : stage === 'access_code' ? 2 : 3;

  return (
    <div className="flex flex-col gap-8">
      <header className="flex flex-col gap-3">
        <span className="text-label uppercase text-accent-primary">Secure File Share</span>
        <h1 className="font-display text-display-l text-fg-primary [font-feature-settings:var(--font-feature-display)]">
          {meta.name ?? 'Secure file share from Apogee'}
        </h1>
        <p className="text-body-l text-fg-secondary">
          Step {stepNum} of 3 — verifying your identity before download.
        </p>
      </header>

      <div className="mx-auto w-full max-w-[560px] flex flex-col">
        <Card padding="comfortable">
          {stage === 'email' ? (
            <form onSubmit={submitEmail} className="flex flex-col gap-5">
              <Input
                label="Step 1 — Email address this share was sent to"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
              />
              <p className="text-body-s text-fg-secondary">
                We&apos;ll confirm this matches the recipient on file. Nothing is sent yet —
                you&apos;ll enter your access code next.
              </p>
              {error ? <ErrorBanner message={error} /> : null}
              <Button
                type="submit"
                variant="primary"
                size="lg"
                loading={submitting}
                disabled={email.trim().length === 0}
                leftIcon={<PadlockIcon size={16} strokeWidth={2.5} />}
                rightIcon={<ArrowRightIcon size={16} strokeWidth={2.5} />}
                data-testid="submit-email"
              >
                {submitting ? 'Verifying…' : 'Continue'}
              </Button>
            </form>
          ) : null}

          {stage === 'access_code' ? (
            <form onSubmit={submitAccessCode} className="flex flex-col gap-5">
              <Input
                label="Step 2 — 8-digit access code from your second email"
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="12345678"
                className="[&_input]:font-mono [&_input]:tracking-widest [&_input]:text-lg"
              />
              <p className="text-body-s text-fg-secondary">
                The sender shared an 8-digit code in a separate email — open that message to find
                it. We&apos;ll send a short verification code to your inbox after this step.
              </p>
              {error ? <ErrorBanner message={error} /> : null}
              <Button
                type="submit"
                variant="primary"
                size="lg"
                loading={submitting}
                disabled={code.length !== 8}
                leftIcon={<PadlockIcon size={16} strokeWidth={2.5} />}
                rightIcon={<ArrowRightIcon size={16} strokeWidth={2.5} />}
                data-testid="submit-access-code"
              >
                {submitting ? 'Verifying…' : 'Continue'}
              </Button>
            </form>
          ) : null}

          {stage === 'totp' ? (
            <form onSubmit={submitTotp} className="flex flex-col gap-5">
              <Input
                label="Step 3 — 6-digit code we just emailed you"
                type="text"
                value={totp}
                onChange={(e) => setTotp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="123456"
                className="[&_input]:font-mono [&_input]:tracking-widest [&_input]:text-lg"
              />
              <p className="text-body-s text-fg-secondary">
                Code expires in {totpExpiresInSeconds ? Math.round(totpExpiresInSeconds / 60) : 5}{' '}
                minutes. Check your inbox (and spam) for the message titled &ldquo;verification
                code&rdquo;.
              </p>
              {error ? <ErrorBanner message={error} /> : null}
              <div className="flex items-center justify-between gap-3">
                <Button
                  type="button"
                  variant="subtle"
                  size="sm"
                  onClick={() => void resendTotp()}
                  disabled={submitting}
                >
                  Resend code
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  loading={submitting}
                  disabled={totp.length !== 6}
                  rightIcon={<ArrowRightIcon size={16} strokeWidth={2.5} />}
                  data-testid="submit-totp"
                >
                  Unlock files
                </Button>
              </div>
            </form>
          ) : null}
        </Card>
        <MonitoredNotice className="mt-4" />
      </div>
    </div>
  );
}

function ErrorBanner({ message }: { message: string }) {
  return (
    <div
      role="alert"
      className="bg-status-danger-muted text-status-danger border border-status-danger/30 rounded-md px-4 py-3 text-body-s"
    >
      {message}
    </div>
  );
}

function FatalScreen({ title, message }: { title: string; message: string }) {
  return (
    <div className="flex flex-col gap-8">
      <header className="flex flex-col gap-3">
        <span className="text-label uppercase text-accent-primary">Secure File Share</span>
        <h1 className="font-display text-display-l text-fg-primary [font-feature-settings:var(--font-feature-display)]">
          {title}
        </h1>
        <p className="text-body-l text-fg-secondary">{message}</p>
      </header>
    </div>
  );
}
